import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  NativeSelect,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@material-ui/core';
import ImportExport from '@material-ui/icons/ImportExport';
import { Title } from '../style';
import { defaultFilters } from '../commands/search/filters';
import { offerCode } from '../../utils/bindings/offerCode';
import {
  exportCommandsReport,
  exportReceptionsComparative,
  exportReturnVouchersReport
} from './exportsActions';

const ExportPaper = styled(Paper)`
  padding: 32px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const commandInitialValues = {
  query: '',
  states: [],
  recipientQuery: '',
  externalReferences: '',
  itemExternalReferences: '',
  itemDescription: '',
  trackingNumbers: '',
  carrierOfferId: '',
  itemEan13List: '',
  imeis: '',
  operationNumber: '',
  carrierId: '',
  createdDateFrom: '',
  createdDateTo: '',
  packedDateFrom: '',
  packedDateTo: '',
  shippedDateFrom: '',
  shippedDateTo: '',
  deliveredDateFrom: '',
  deliveredDateTo: '',
  sentByMe: '1',
  format: 'excel'
};

const returnVoucherInitialValues = {
  processedDateFrom: '',
  processedDateTo: ''
};

const receptionInitialValues = {
  actualDeliveryDateFrom: '',
  actualDeliveryDateTo: ''
};

const ExportDialog = ({ title, open, onClose, onExport, children }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogActions>
      <Button color="secondary" variant="contained" onClick={onClose}>
        Annuler
      </Button>
      <Button color="primary" variant="contained" onClick={onExport}>
        Export
        <ImportExport />
      </Button>
    </DialogActions>
  </Dialog>
);

const DateField = ({ name, label, value, onChange }) => (
  <TextField
    fullWidth
    id={name}
    name={name}
    label={label}
    type="date"
    value={value}
    onChange={onChange}
    InputLabelProps={{ shrink: true }}
  />
);

const CommandFilters = ({ values, usedCarrier, onChange, onStatesChange }) => (
  <Grid container spacing={3}>
    <Grid item md={6} xs={12}>
      <FormControl component="fieldset">
        <FormLabel component="legend">Type</FormLabel>
        <RadioGroup name="sentByMe" value={values.sentByMe} onChange={onChange}>
          <FormControlLabel
            value="1"
            control={<Radio color="primary" />}
            label="Expéditions"
          />
          <FormControlLabel
            value="0"
            control={<Radio color="primary" />}
            label="Réceptions"
          />
        </RadioGroup>
      </FormControl>
    </Grid>
    <Grid item md={6} xs={12}>
      <FormControl component="fieldset">
        <FormLabel component="legend">Format</FormLabel>
        <RadioGroup name="format" value={values.format} onChange={onChange}>
          <FormControlLabel
            value="excel"
            control={<Radio color="primary" />}
            label="Excel"
          />
          <FormControlLabel
            value="csv"
            control={<Radio color="primary" />}
            label="CSV"
          />
        </RadioGroup>
      </FormControl>
    </Grid>
    <Grid item md={6} xs={12}>
      <FormControl fullWidth>
        <InputLabel htmlFor="states">État des commandes</InputLabel>
        <Select
          multiple
          value={values.states}
          onChange={onStatesChange}
          input={<Input id="states" />}
          renderValue={selected =>
            selected
              .map(
                item =>
                  defaultFilters.find(filter => filter.value === item).label
              )
              .join(', ')
          }
        >
          {defaultFilters.map(item => (
            <MenuItem key={item.value} value={item.value}>
              <Checkbox checked={values.states.indexOf(item.value) > -1} />
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item md={6} xs={12}>
      <TextField
        fullWidth
        name="query"
        label="Recherche"
        value={values.query}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="recipientQuery"
        label="Destinataire"
        value={values.recipientQuery}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="externalReferences"
        label="Référence de commande"
        value={values.externalReferences}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="itemExternalReferences"
        label="Référence de produit"
        value={values.itemExternalReferences}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="itemDescription"
        label="Description produit"
        value={values.itemDescription}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="trackingNumbers"
        label="N° de suivi"
        value={values.trackingNumbers}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <FormControl fullWidth>
        <InputLabel>Offre de livraison</InputLabel>
        <NativeSelect
          name="carrierOfferId"
          value={values.carrierOfferId}
          onChange={onChange}
        >
          <option aria-label="None" value="" />
          {offerCode.map(data => (
            <option key={data.id} value={data.value}>
              {data.name}
            </option>
          ))}
        </NativeSelect>
      </FormControl>
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="itemEan13List"
        label="EAN 13"
        value={values.itemEan13List}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="imeis"
        label="IMEI"
        value={values.imeis}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <TextField
        fullWidth
        name="operationNumber"
        label="ID Opération"
        value={values.operationNumber}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <FormControl fullWidth>
        <InputLabel>Transporteur</InputLabel>
        <NativeSelect
          name="carrierId"
          value={values.carrierId}
          onChange={onChange}
        >
          <option aria-label="None" value="" />
          {usedCarrier.map(data => (
            <option key={data.id} value={data.id}>
              {data.name}
            </option>
          ))}
        </NativeSelect>
      </FormControl>
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="createdDateFrom"
        label="Date de création minimum"
        value={values.createdDateFrom}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="createdDateTo"
        label="Date de création maximum"
        value={values.createdDateTo}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="packedDateFrom"
        label="Date d'emballage minimum"
        value={values.packedDateFrom}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="packedDateTo"
        label="Date d'emballage maximum"
        value={values.packedDateTo}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="shippedDateFrom"
        label="Date d'expédition minimum"
        value={values.shippedDateFrom}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="shippedDateTo"
        label="Date d'expédition maximum"
        value={values.shippedDateTo}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="deliveredDateFrom"
        label="Date de livraison minimum"
        value={values.deliveredDateFrom}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={3} xs={12}>
      <DateField
        name="deliveredDateTo"
        label="Date de livraison maximum"
        value={values.deliveredDateTo}
        onChange={onChange}
      />
    </Grid>
  </Grid>
);

const DateRangeFilters = ({
  values,
  fromName,
  toName,
  fromLabel,
  toLabel,
  onChange
}) => (
  <Grid container spacing={3}>
    <Grid item md={6} xs={12}>
      <DateField
        name={fromName}
        label={fromLabel}
        value={values[fromName]}
        onChange={onChange}
      />
    </Grid>
    <Grid item md={6} xs={12}>
      <DateField
        name={toName}
        label={toLabel}
        value={values[toName]}
        onChange={onChange}
      />
    </Grid>
  </Grid>
);

const Exports = ({
  dispatchExportCommandsReport,
  dispatchExportReturnVouchersReport,
  dispatchExportReceptionsComparative,
  usedCarrier
}) => {
  const [openDialog, setOpenDialog] = useState(null);
  const [commandValues, setCommandValues] = useState(commandInitialValues);
  const [returnVoucherValues, setReturnVoucherValues] = useState(
    returnVoucherInitialValues
  );
  const [receptionValues, setReceptionValues] = useState(
    receptionInitialValues
  );

  const handleCommandChange = event => {
    const { name, value } = event.target;
    setCommandValues(oldValues => ({ ...oldValues, [name]: value }));
  };

  const handleReturnVoucherChange = event => {
    const { name, value } = event.target;
    setReturnVoucherValues(oldValues => ({ ...oldValues, [name]: value }));
  };

  const handleReceptionChange = event => {
    const { name, value } = event.target;
    setReceptionValues(oldValues => ({ ...oldValues, [name]: value }));
  };

  const handleCommandsExport = () => {
    const companyFilter =
      commandValues.sentByMe === '1'
        ? { senderCompany: 'mine' }
        : { recipientCompany: 'mine' };
    dispatchExportCommandsReport({
      ...commandValues,
      ...companyFilter,
      sentByMe: +commandValues.sentByMe
    });
    setOpenDialog(null);
  };

  const handleReturnVouchersExport = () => {
    dispatchExportReturnVouchersReport(returnVoucherValues);
    setOpenDialog(null);
  };

  const handleReceptionsExport = () => {
    dispatchExportReceptionsComparative(receptionValues);
    setOpenDialog(null);
  };

  return (
    <div>
      <Title>Export</Title>
      <ExportPaper>
        <Box mb={3}>Sélectionnez le rapport à exporter.</Box>
        <ButtonContainer>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setOpenDialog('commands')}
          >
            Export commandes
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setOpenDialog('returnVouchers')}
          >
            Export bons de retours traités
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setOpenDialog('receptions')}
          >
            Export réceptions
          </Button>
        </ButtonContainer>
      </ExportPaper>

      <ExportDialog
        title="Export commandes"
        open={openDialog === 'commands'}
        onClose={() => setOpenDialog(null)}
        onExport={handleCommandsExport}
      >
        <CommandFilters
          values={commandValues}
          usedCarrier={usedCarrier}
          onChange={handleCommandChange}
          onStatesChange={({ target: { value } }) =>
            setCommandValues(oldValues => ({ ...oldValues, states: value }))
          }
        />
      </ExportDialog>

      <ExportDialog
        title="Export bons de retours traités"
        open={openDialog === 'returnVouchers'}
        onClose={() => setOpenDialog(null)}
        onExport={handleReturnVouchersExport}
      >
        <DateRangeFilters
          values={returnVoucherValues}
          fromName="processedDateFrom"
          toName="processedDateTo"
          fromLabel="Date de traitement minimum"
          toLabel="Date de traitement maximum"
          onChange={handleReturnVoucherChange}
        />
      </ExportDialog>

      <ExportDialog
        title="Export réceptions"
        open={openDialog === 'receptions'}
        onClose={() => setOpenDialog(null)}
        onExport={handleReceptionsExport}
      >
        <DateRangeFilters
          values={receptionValues}
          fromName="actualDeliveryDateFrom"
          toName="actualDeliveryDateTo"
          fromLabel="Date de réception minimum"
          toLabel="Date de réception maximum"
          onChange={handleReceptionChange}
        />
      </ExportDialog>
    </div>
  );
};

ExportDialog.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

DateField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

CommandFilters.propTypes = {
  values: PropTypes.object.isRequired,
  usedCarrier: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onStatesChange: PropTypes.func.isRequired
};

DateRangeFilters.propTypes = {
  values: PropTypes.object.isRequired,
  fromName: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

Exports.propTypes = {
  dispatchExportCommandsReport: PropTypes.func.isRequired,
  dispatchExportReturnVouchersReport: PropTypes.func.isRequired,
  dispatchExportReceptionsComparative: PropTypes.func.isRequired,
  usedCarrier: PropTypes.array.isRequired
};

const mapStateToProps = state => ({
  usedCarrier: state.init.usedCarrier || []
});

const mapDispatchToProps = dispatch => ({
  dispatchExportCommandsReport: data => dispatch(exportCommandsReport(data)),
  dispatchExportReturnVouchersReport: data =>
    dispatch(exportReturnVouchersReport(data)),
  dispatchExportReceptionsComparative: data =>
    dispatch(exportReceptionsComparative(data))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Exports);
